/**
 * Canonical transaction access layer -- read and write, one file.
 *
 * (Previously two files, TransactionFetchService.js and
 * TransactionWriteService.js, split the day this module was first written.
 * Merged back into one, same day, on the simple principle that read and
 * write for the same entity is one concern, not two: "transactions" is one
 * noun, and there was no real reason a caller needing both had to import two
 * files with two different naming conventions to get them.)
 *
 * All consumers of transaction data -- reads or writes -- should go through
 * this service rather than downloading/parsing CSV directly, hand-building
 * query params, or making axios calls of their own.
 *
 * === READS ===
 *
 * fetchScopedTransactions() calls the equity-transactions-scoped Lambda. Its
 * own job is narrow: call the endpoint, optionally decompress the payload,
 * return the rows verbatim. It does no parsing, normalization, or derivation
 * of its own. The guarantees below belong to the transaction PIPELINE as a
 * whole (equity-transactions-scoped.py), not to this file:
 *  - Header recognition (preferred) with fixed-column fallback
 *  - Header alias normalization (case, spaces, camelCase, common variants)
 *  - Precision merge (sharePrice + exactPrice -> highest precision sharePrice)
 *  - Numeric/date normalization
 *  - Omission of read-only/import-only columns
 *  - Preservation of custom (unrecognized) columns
 *  - Consistent transaction objects for every client
 * The browser should not contain client-specific CSV parsing logic -- this
 * service (backed by the Lambda) is what replaces it.
 *
 * === WRITES ===
 *
 * Before this module existed, "create/update/delete a transaction" was
 * hand-rolled independently in at least four places: ledgers.js's
 * newTransactionModal, ledgers.js's editTransactionModal, ledgers.js's bulk
 * CSV-reimport updateThisTransaction()/deleteThisTransaction(), and
 * gift-transfer.js's historical Tax Basis inline edit. Each copy built its
 * own query-param object by hand, and they'd already drifted:
 * editTransactionModal sent the tax basis param as `tax_basis` (matching what
 * client/transactions.py actually reads), while the bulk CSV-reimport path
 * sent `taxBasis` (camelCase) -- a key the backend never reads, so it fell
 * through to `params.get("tax_basis", "")`'s empty-string default and
 * SILENTLY BLANKED Tax Basis on every row processed through that tool. Fixed
 * here by having exactly one function build these params, ever.
 *
 * Two distinct backends, both covered:
 *  - JSON-native clients: a direct PUT/PATCH/DELETE per transaction, hitting
 *    client/transactions.py (see createTransaction/patchTransaction/
 *    deleteTransaction below). Note client/transactions.py's PATCH is a full-
 *    record REPLACE, not a merge -- every field must be sent every time, or
 *    the backend will blank it. This module always sends the full shape.
 *  - EXTERNAL_DATA (CSV-backed) clients: transactions live in a client-owned
 *    CSV file, not as JSON rows. This module owns downloading, mutating, and
 *    re-uploading that CSV (queueExternalWrite/flushExternalQueue), ported
 *    from ledgers.js's former `externalTxnStore` (previously a private
 *    closure inside that one page's script, unusable by any other page --
 *    Gift Transfer needed the exact same behavior and had no way to reach
 *    it). NOTE: this duplicates some of what transaction-schema.js's
 *    fromCSVRow()/toCSVRow() do, but is NOT a safe drop-in replacement for
 *    it -- _recordToRow() below writes into a client's actual/arbitrary
 *    existing header layout and preserves unknown custom columns, while
 *    toCSVRow() always emits a fixed 14-column row. READ_ONLY_HEADERS here is
 *    also broader than schema.js's list. See transaction-schema.js's file
 *    header for the full explanation before attempting to consolidate these.
 *
 * The write functions do NOT know about any caller's page-local display
 * cache (ledgers.js's `allTransactions`, gift-transfer.js's
 * `giftTransactions`, etc.) or its modals/toasts. They mutate the one shared
 * source of truth -- `clientData.equity[shareClass].transactions` -- and
 * return the transaction just written; refreshing whatever the caller
 * renders from that is the caller's job.
 */

import { urlTransactions, authUploadUrl, urlEquityTransactionsScoped } from '../helpers/api-urls.js';
import { secureFormData, uuidv4 } from '../helpers/string-utils.js';

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

async function _decompressGzip(base64Data) {
	const bytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
	const ds = new DecompressionStream('gzip');
	const writer = ds.writable.getWriter();
	writer.write(bytes);
	writer.close();
	const chunks = [];
	const reader = ds.readable.getReader();
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
	}
	const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
	const merged = new Uint8Array(totalLen);
	let offset = 0;
	for (const chunk of chunks) {
		merged.set(chunk, offset);
		offset += chunk.length;
	}
	return new TextDecoder().decode(merged);
}

/**
 * Fetch canonical transaction rows using optional userid and share-class filters.
 *
 * @param {object} opts
 * @param {string}   opts.client   - client identifier
 * @param {string}   opts.userid   - the authenticated user (for auth check)
 * @param {string}   opts.cookie   - session cookie value
 * @param {string[]} opts.userids  - userids whose transactions to return; pass [] to get the full set (admin only)
 * @param {string[]} opts.shareClasses  - share classes whose transactions to return; pass [] to return all classes
 * @returns {Promise<object[]>} canonical transaction rows
 */
export async function fetchScopedTransactions({ client, userid, cookie, userids = [], shareClasses = [] }) {
	const params = new URLSearchParams({ client, userid });
	if (cookie) params.set('cookie', cookie);
	if (userids.length) params.set('userids', userids.join(','));
	if (shareClasses.length) params.set('shareClasses', shareClasses.join(','));

	// credentials: 'include' lets the browser send the HttpOnly nth.IdToken cookie
	// header in cross-origin requests -- needed on www.staging where the cookie is
	// not JS-readable so the `cookie` query param above is empty. Matches the axios
	// `withCredentials = !cookie` pattern used elsewhere; the matching backend
	// endpoint sets Access-Control-Allow-Credentials: true.
	const response = await fetch(`${urlEquityTransactionsScoped}?${params}`, { credentials: 'include' });
	if (!response.ok) throw new Error(`equity-transactions-scoped ${response.status}`);

	const body = await response.json();

	if (body.compressed) {
		const json = await _decompressGzip(body.data);
		return JSON.parse(json).rows || [];
	}
	return body.rows || [];
}

// -----------------------------------------------------------------------------
// WRITE -- JSON-native single-transaction CRUD
// -----------------------------------------------------------------------------

/**
 * Builds the full param set client/transactions.py expects, from a canonical
 * transaction object. Every field is sent every time (see module docstring:
 * this backend replaces the whole record, it doesn't merge).
 */
function _buildParams({ userid, cookie, client, shareClass, transaction, nrid, customColumns }) {
	const params = {
		userid,
		cookie,
		client,
		class_name: shareClass,
		date: transaction.date || '',
		notes: transaction.notes || '',
		security_type: transaction.securityType || '',
		share_price: transaction.sharePrice || '',
		quantity: transaction.quantity || '',
		sender_id: transaction.sender || '',
		receiver_id: transaction.receiver || '',
		sender_name: transaction.senderName || '',
		receiver_name: transaction.receiverName || '',
	};

	if (transaction.expDate) params.expDate = transaction.expDate;
	if (transaction.strikePrice) params.strike_price = transaction.strikePrice;
	// Deliberately not gated behind `if (transaction.taxBasis)` the way the
	// other optional fields are: an explicit, intentional clear (editing a
	// row back to blank) must still reach the backend as `tax_basis: ''`,
	// not be silently omitted -- omission and "" behave the same server-side
	// today (see module docstring), but don't rely on that ambiguity holding.
	params.tax_basis = transaction.taxBasis || '';

	if (nrid) params.nrid = nrid;
	if (customColumns) params.custom_columns = JSON.stringify(customColumns);

	return params;
}

/** Every custom-column key currently defined for this client (multi-column,
 * 2026-08-21) -- [] when none are configured. */
function _customColumnKeys(clientData) {
	const defined = clientData?.transaction_custom_columns;
	if (!Array.isArray(defined)) return [];
	return defined.filter((c) => c && c.key).map((c) => c.key);
}

/** Pull this client's custom-column values off a transaction record (keyed
 * flat on the record itself, e.g. transaction.gift_fmv) into a {key: value}
 * object for the wire payload -- undefined when the client has none defined. */
function _extractCustomColumns(clientData, transaction) {
	const keys = _customColumnKeys(clientData);
	if (!keys.length) return undefined;
	const out = {};
	for (const k of keys) out[k] = transaction[k] ?? '';
	return out;
}

/** Batch-call version of _extractCustomColumns -- nests each record's custom
 * values under record.customColumns for the wire payload (see
 * client/transactions.py's _merge_custom_columns). No-op array copy when the
 * client has no custom columns defined. */
function _withWireCustomColumns(clientData, records) {
	const keys = _customColumnKeys(clientData);
	if (!keys.length) return records;
	return records.map((r) => ({ ...r, customColumns: _extractCustomColumns(clientData, r) }));
}

function _applyLocalWrite({ clientData, shareClass, transaction }) {
	if (!clientData.equity[shareClass].transactions) clientData.equity[shareClass].transactions = [];
	const txns = clientData.equity[shareClass].transactions;
	const idx = txns.findIndex((x) => x.nrid === transaction.nrid);
	if (idx >= 0) txns[idx] = transaction;
	else txns.push(transaction);
}

function _persistSession({ client, clientData }) {
	sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
}

/**
 * Create a new transaction for a JSON-native client.
 *
 * @param {object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {object} opts.clientData - mutated in place: the new transaction is
 *   pushed onto clientData.equity[shareClass].transactions and sessionStorage
 *   is refreshed.
 * @param {string} opts.shareClass
 * @param {object} opts.transaction - canonical transaction fields (date,
 *   notes, securityType, sharePrice, quantity, sender, receiver, senderName,
 *   receiverName, expDate?, strikePrice?, taxBasis?). No nrid -- the backend
 *   assigns one.
 * @returns {Promise<object>} the created transaction, with nrid populated.
 */
export async function createTransaction({ userid, cookie, client, clientData, shareClass, transaction }) {
	const customColumns = _extractCustomColumns(clientData, transaction);
	const params = _buildParams({ userid, cookie, client, shareClass, transaction, customColumns });

	const res = await axios.put(urlTransactions, { body: 'body' }, { params });

	// GH #6140: backend misrouting used to return HTTP 200 with no nrid for a
	// write that never happened. Fixed server-side, but guard against it here too.
	if (!res.data?.nrid) throw new Error('createTransaction: backend did not return a nrid -- the transaction was not saved.');

	const created = { ...transaction, shareClass, nrid: res.data.nrid };
	_applyLocalWrite({ clientData, shareClass, transaction: created });
	_persistSession({ client, clientData });
	return created;
}

/**
 * Sanitize already-resolved fields into the canonical full-record
 * transaction shape used everywhere in this codebase (the same shape
 * createTransaction/patchTransaction/queueExternalWrite all expect).
 *
 * History (#5971, Round Two, 2026-07-30): "create a new transaction" used to
 * be built independently in ledgers.js's createNewTransaction (addTransaction
 * Form + CSV-reimport bulk-add) and gift-transfer.js's createNewTransaction
 * (bulk gift-CSV importer) -- two copies of the same sanitize-then-shape
 * logic that had already drifted (Ledgers rejected negative Tax Basis,
 * Gift Transfer allowed it) by the time this was noticed. This function is
 * the one place that sanitization + shaping happens now.
 *
 * Deliberately NARROW in scope -- an earlier attempt in this same session
 * also tried to unify sender/receiver/name resolution and date-to-unix
 * conversion across both callers, and that briefly introduced a real bug:
 * Gift Transfer's caller already hands over unix-seconds dates (converted
 * from CSV row data before this function ever sees them), so running them
 * through dateStringToUnix() a second time silently blanked every imported
 * gift's date (dateStringToUnix does strict format-matched parsing and
 * returns '' on anything that doesn't look like a date string -- an
 * already-numeric unix value doesn't). Ledgers' <select>-based sender/
 * receiver values are also "Name | id" pipe-formatted and need splitting;
 * Gift Transfer's CSV rows already carry bare ids and separate name fields.
 * Both of those are genuinely caller-specific and stay each caller's job:
 * reading raw input, sender/receiver/Issuer-counterparty resolution,
 * date-to-unix conversion, cap-table-adjustment UI/side-effects, modal
 * closing, table re-rendering, and progress counters.
 *
 * What IS shared here (the part that had actually drifted / was duplicated
 * verbatim): DOMPurify sanitization and character-stripping of every field,
 * with ONE regex per field applied consistently, and the full-record shape
 * with gated optionals (expDate/strikePrice/taxBasis) -- matching
 * _buildParams' own identical gating convention above.
 *
 * @param {Object} fields - already-resolved, NOT YET sanitized:
 *   { date, expDate, notes, shareClass, securityType, strikePrice,
 *     sharePrice, taxBasis, quantity, sender, receiver, senderName,
 *     receiverName }. date/expDate must already be unix-seconds strings (or
 *   '') -- this function does no date parsing/conversion. sender/receiver
 *   must already be bare ids (or '' for whichever side is the Issuer).
 * @returns {Object} the canonical transaction record -- ready to pass as
 *   `transaction` to createTransaction()/patchTransaction(), or as `record`
 *   to queueExternalWrite().
 */
export function buildTransactionRecord(fields) {
	const shareClass = DOMPurify.sanitize(String(fields.shareClass ?? '')).replace(/&amp;/g, '&');

	const record = {
		date: DOMPurify.sanitize(String(fields.date ?? '')),
		notes: DOMPurify.sanitize(String(fields.notes ?? '')),
		securityType: DOMPurify.sanitize(String(fields.securityType ?? '')),
		shareClass,
		sharePrice: DOMPurify.sanitize(String(fields.sharePrice ?? '').replace(/[^.\d]/g, '')),
		quantity: DOMPurify.sanitize(String(fields.quantity ?? '').replace(/[^-.\d]/g, '')),
		sender: DOMPurify.sanitize(String(fields.sender ?? '')),
		receiver: DOMPurify.sanitize(String(fields.receiver ?? '')),
		senderName: DOMPurify.sanitize(String(fields.senderName ?? '')),
		receiverName: DOMPurify.sanitize(String(fields.receiverName ?? '')),
	};

	const expDate = DOMPurify.sanitize(String(fields.expDate ?? ''));
	const strikePrice = DOMPurify.sanitize(String(fields.strikePrice ?? '').replace(/[^.\d]/g, ''));
	// Negative Tax Basis is not allowed (per Chris, 2026-07-30).
	const taxBasis = DOMPurify.sanitize(String(fields.taxBasis ?? '').replace(/[^.\d]/g, ''));

	if (expDate) record.expDate = expDate;
	if (strikePrice) record.strikePrice = strikePrice;
	if (taxBasis) record.taxBasis = taxBasis;

	return record;
}

/**
 * Update an existing transaction for a JSON-native client. Full-record
 * replace -- transaction must include every field you want preserved, not
 * just the one(s) that changed (see module docstring).
 *
 * @param {object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {object} opts.clientData - mutated in place, same as createTransaction.
 * @param {string} opts.shareClass
 * @param {object} opts.transaction - full canonical transaction fields,
 *   including the existing nrid.
 * @returns {Promise<object>} the updated transaction (echoes `transaction`;
 *   the backend response body isn't the source of truth here, matching the
 *   pre-existing editTransactionModal convention this replaces).
 */
export async function patchTransaction({ userid, cookie, client, clientData, shareClass, transaction }) {
	if (!transaction.nrid) throw new Error('patchTransaction: transaction.nrid is required');

	const customColumns = _extractCustomColumns(clientData, transaction);
	const params = _buildParams({ userid, cookie, client, shareClass, transaction, nrid: transaction.nrid, customColumns });

	await axios.patch(urlTransactions, { body: 'body' }, { params });

	const updated = { ...transaction, shareClass };
	_applyLocalWrite({ clientData, shareClass, transaction: updated });
	_persistSession({ client, clientData });
	return updated;
}

/**
 * Delete an existing transaction for a JSON-native client.
 *
 * @param {object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {object} opts.clientData - mutated in place: the transaction is
 *   removed from clientData.equity[shareClass].transactions.
 * @param {string} opts.shareClass
 * @param {string} opts.nrid
 */
export async function deleteTransaction({ userid, cookie, client, clientData, shareClass, nrid }) {
	await axios.delete(urlTransactions, { params: { userid, cookie, client, class_name: shareClass, nrid } });

	if (clientData.equity[shareClass]?.transactions) {
		clientData.equity[shareClass].transactions = clientData.equity[shareClass].transactions.filter((x) => x.nrid !== nrid);
	}
	_persistSession({ client, clientData });
}

/**
 * Create many transactions for a JSON-native client in ONE network call
 * (was N sequential createTransaction() calls).
 *
 * @param {object} opts
 * @param {object} opts.clientData - mutated in place, same as createTransaction.
 * @param {object[]} opts.records - canonical transaction records (buildTransactionRecord()
 *   shape) including shareClass. No nrid -- the backend assigns one per row.
 * @returns {Promise<{created: object[], failed: object[]}>}
 */
export async function createTransactionsBatch({ userid, cookie, client, clientData, records }) {
	if (!records?.length) return { created: [], failed: [] };

	const wireRecords = _withWireCustomColumns(clientData, records);
	const res = await axios.put(urlTransactions, { transactions: wireRecords }, { params: { userid, cookie, client } });

	// Pair the i-th "created" result for a class with the i-th record we submitted for it.
	const nextIndexByClass = {};
	const recordsByClass = {};
	for (const r of records) (recordsByClass[r.shareClass] ||= []).push(r);

	const created = [];
	for (const row of res.data.created || []) {
		const i = nextIndexByClass[row.shareClass] || 0;
		nextIndexByClass[row.shareClass] = i + 1;
		const source = recordsByClass[row.shareClass]?.[i] || {};
		const full = { ...source, shareClass: row.shareClass, nrid: row.nrid };
		created.push(full);
		_applyLocalWrite({ clientData, shareClass: row.shareClass, transaction: full });
	}

	_persistSession({ client, clientData });
	return { created, failed: res.data.failed || [] };
}

/**
 * Update many transactions for a JSON-native client in ONE network call
 * (was N sequential patchTransaction() calls). Full-record replace per row.
 *
 * @param {object} opts
 * @param {object} opts.clientData - mutated in place for every row found and updated.
 * @param {object[]} opts.records - full canonical transaction records, each with shareClass + nrid.
 * @returns {Promise<{updated: object[], not_found: object[], failed: object[]}>}
 */
export async function patchTransactionsBatch({ userid, cookie, client, clientData, records }) {
	if (!records?.length) return { updated: [], not_found: [], failed: [] };

	const wireRecords = _withWireCustomColumns(clientData, records);
	const res = await axios.patch(urlTransactions, { transactions: wireRecords }, { params: { userid, cookie, client } });

	const byNrid = new Map(records.map((r) => [r.nrid, r]));
	const updated = [];
	for (const row of res.data.updated || []) {
		const source = byNrid.get(row.nrid);
		if (!source) continue;
		const full = { ...source, shareClass: row.shareClass };
		updated.push(full);
		_applyLocalWrite({ clientData, shareClass: row.shareClass, transaction: full });
	}

	_persistSession({ client, clientData });
	return { updated, not_found: res.data.not_found || [], failed: res.data.failed || [] };
}

/**
 * Delete many transactions for a JSON-native client in ONE network call
 * (was N sequential deleteTransaction() calls).
 *
 * @param {object} opts
 * @param {object} opts.clientData - mutated in place, rows removed.
 * @param {object[]} opts.records - each {shareClass, nrid}.
 * @returns {Promise<{deleted: object[], not_found: object[], failed: object[]}>}
 */
export async function deleteTransactionsBatch({ userid, cookie, client, clientData, records }) {
	if (!records?.length) return { deleted: [], not_found: [], failed: [] };

	const res = await axios.delete(urlTransactions, { params: { userid, cookie, client }, data: { transactions: records } });

	for (const row of res.data.deleted || []) {
		const txns = clientData.equity[row.shareClass]?.transactions;
		if (txns) clientData.equity[row.shareClass].transactions = txns.filter((x) => x.nrid !== row.nrid);
	}

	_persistSession({ client, clientData });
	return { deleted: res.data.deleted || [], not_found: res.data.not_found || [], failed: res.data.failed || [] };
}

// -----------------------------------------------------------------------------
// WRITE -- EXTERNAL_DATA (CSV-backed) queue/flush
//
// Ported from ledgers.js's former `externalTxnStore`. Module-level state is
// intentional and matches the original: exactly one client's transactions CSV
// is ever in flight in a browser tab at a time (each admin page -- Ledgers,
// Gift Transfer's host page, etc. -- is its own top-level bundle; none of them
// run two of these concurrently), the same assumption client-flags.js already
// makes for its own module-level `_client` singleton.
// -----------------------------------------------------------------------------

let _loaded = false;
let _loading = false;
let _flushing = false;

let _rows = null;
let _header = null;
let _fieldIndex = null;

let _queue = [];

const CANONICAL_HEADERS = [
	'Share Class',
	'Date',
	'Security Type',
	'Notes',
	'nrid',
	'Sender Name',
	'Sender',
	'Receiver Name',
	'Receiver',
	'Quantity',
	'Share Price',
	'Strike Price',
	'Expiration Date',
	'Tax Basis',
];

const FIELD_TO_HEADER = {
	shareClass: 'Share Class',
	date: 'Date',
	securityType: 'Security Type',
	notes: 'Notes',
	nrid: 'nrid',
	senderName: 'Sender Name',
	sender: 'Sender',
	receiverName: 'Receiver Name',
	receiver: 'Receiver',
	quantity: 'Quantity',
	sharePrice: 'Share Price',
	strikePrice: 'Strike Price',
	expDate: 'Expiration Date',
	taxBasis: 'Tax Basis',
};

// Alias-aware column resolution (added 2026-08-03, #5980 Fishbeck).
//
// Mirrors NewServerless/core-data/client/equity-transactions-scoped.py's
// STANDARD_TRANSACTION_HEADER_ALIASES/ALIAS_PRIORITY_OVERRIDES as closely as
// two different languages/repos allow -- keep these two tables in sync. The
// backend already tolerates a client's physical CSV using any of these
// alternate header spellings on READ. This write path used to know nothing
// about that: _headerIndex/_set matched ONLY the exact literal
// FIELD_TO_HEADER text, so a client whose real file used one of these
// aliases (e.g. "Sender Email" instead of "Sender", or "Description"
// instead of "Notes") got a brand-new duplicate canonical column silently
// appended on every write, leaving their actual existing column untouched --
// exactly the malformed row in #5980 (shareholder email written into a new
// "Sender" column, the file's real "Sender Email" column left blank).
const HEADER_ALIASES = {
	shareClass: ['class', 'share class', 'entity', 'property', 'fund'],
	date: ['date', 'transaction date', 'trans date'],
	securityType: ['security type', 'type'],
	notes: ['notes', 'description', 'desc'],
	nrid: ['nrid'],
	senderName: ['sender name', 'sender'],
	sender: ['sender', 'sender email'],
	receiverName: ['receiver name', 'recipient name', 'receiver', 'recipient'],
	receiver: ['receiver', 'receiver email', 'recipient', 'recipient email'],
	quantity: ['quantity', 'qty', 'shares', 'units', 'value'],
	// 'price paid' added 2026-08-05 per Chris/Stuart: deliberately promoted as
	// a *clarifying* synonym, not just another spelling -- it reinforces that
	// Share Price is what actually changed hands in THIS transaction, as
	// distinct from Tax Basis (which can carry over from a prior owner, e.g.
	// on a gift, and needn't match what was paid here at all).
	sharePrice: ['price', 'share price', 'price paid'],
	strikePrice: ['strike price', 'strike', 'hurdle', 'threshold'],
	expDate: ['expiration date', 'exp date', 'expiration'],
	taxBasis: ['tax basis', 'cost basis', 'basis'],
};

// Mirrors the backend's ALIAS_PRIORITY_OVERRIDES exactly, including its
// 2026-08-03 fix (#5980): sender/receiver's email-specific alias reserves
// the identifier column first (whole header row, not by position), THEN
// senderName/receiverName's own dedicated Name alias reserves the display-
// name column next -- before the generic single-pass fallback below ever
// lets a leftover bare "Sender"/"Receiver" column double up as both. Order
// matters here (object key order = processing order): senderName/
// receiverName must be listed after sender/receiver so the email-specific
// column is already claimed by the time the bare column would otherwise be
// considered for either purpose.
const HEADER_ALIAS_PRIORITY = {
	sender: ['sender email', 'sender'],
	receiver: ['receiver email', 'recipient email', 'receiver', 'recipient'],
	senderName: ['sender name', 'sender'],
	receiverName: ['receiver name', 'recipient name', 'receiver', 'recipient'],
};

/**
 * Resolve every known field to a column index in the given physical header
 * row, using the SAME two-pass algorithm as the backend's
 * _build_standard_transaction_header_map(): a priority pass (HEADER_ALIAS_PRIORITY,
 * in order) that reserves the more specific alias across the whole row
 * first, then a generic pass (HEADER_ALIASES) that gives any
 * still-unresolved field the first unclaimed column matching any of its
 * aliases, by column position. Fields with no match in either pass are
 * simply absent from the returned map -- the caller decides whether to
 * append a new canonical column for them (see _rebuildHeaderIndexes).
 *
 * @param {string[]} header
 * @param {Object<string, string[]>} [extraAliases] - dynamic, per-client
 *   aliases that can't live in the fixed HEADER_ALIASES table above -- one
 *   entry per custom column, keyed by that column's stable key, valued with
 *   its current admin-chosen label (e.g. {gift_fmv: ['Gift FMV']}), not a
 *   fixed canonical name -- see _customColumnAliases() below, which builds
 *   this from clientData for the production caller (_rebuildHeaderIndexes).
 *   Only the CURRENT label is ever recognized: this function does no
 *   caching, so relabeling naturally stops matching the old text without
 *   any extra bookkeeping.
 */
function _resolveHeaderMap(header, extraAliases) {
	const normalized = (header || []).map((h) => _normHeader(h));
	const fieldIndex = {};
	const claimed = new Set();

	Object.keys(HEADER_ALIAS_PRIORITY).forEach((field) => {
		for (const alias of HEADER_ALIAS_PRIORITY[field]) {
			const wanted = _normHeader(alias);
			const idx = normalized.findIndex((h, i) => h === wanted && !claimed.has(i));
			if (idx >= 0) {
				fieldIndex[field] = idx;
				claimed.add(idx);
				break;
			}
		}
	});

	Object.keys(HEADER_ALIASES).forEach((field) => {
		if (field in fieldIndex) return;
		const wantedSet = new Set(HEADER_ALIASES[field].map(_normHeader));
		const idx = normalized.findIndex((h, i) => wantedSet.has(h) && !claimed.has(i));
		if (idx >= 0) {
			fieldIndex[field] = idx;
			claimed.add(idx);
		}
	});

	// One entry per custom column, keyed by its stable key (multi-column,
	// 2026-08-21) -- was a single hardcoded 'customColumn' check.
	Object.keys(extraAliases || {}).forEach((field) => {
		if (field in fieldIndex) return;
		const aliases = extraAliases[field];
		if (!aliases?.length) return;
		const wantedSet = new Set(aliases.map(_normHeader));
		const idx = normalized.findIndex((h, i) => wantedSet.has(h) && !claimed.has(i));
		if (idx >= 0) {
			fieldIndex[field] = idx;
			claimed.add(idx);
		}
	});

	return fieldIndex;
}

/**
 * Build _resolveHeaderMap()'s extraAliases arg from a client's current
 * custom-column config: one {key: [label]} entry per transaction_custom_columns
 * entry. [] transaction_custom_columns (the client has none defined) yields
 * {} -- no custom columns are then recognized by _resolveHeaderMap, matching
 * "no config, no field" for every other canonical field.
 */
function _customColumnAliases(clientData) {
	const aliases = {};
	for (const col of clientData?.transaction_custom_columns || []) {
		const label = col?.label;
		if (col?.key && label) aliases[col.key] = [label];
	}
	return aliases;
}

// Read-only/export-only columns: never written back into the physical CSV,
// regardless of what a caller's record contains. Mirrors equity.py's
// strip_derived_fields() intent, for the CSV write path.
const READ_ONLY_HEADERS = new Set([
	'cert',
	'certnumber',
	'certificate',
	'certificatenumber',
	'certsort',
	'referenceprice',
	'refprice',
	'vendorcode',
	'qtyxprice',
	'qtyxrefprice',
	'qtyxexactprice',
	'qtyrefprice',
	'qtyexactprice',
	'exactprice',
	'exactcost',
	'referencecost',
	'qtyexactcost',
	'qtyrefcost',
]);

function _normHeader(h) {
	return String(h || '')
		.trim()
		.toLowerCase()
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[^a-z0-9]/g, '');
}

function _parseCsv(txt) {
	const parsed = Papa.parse(txt || '', { skipEmptyLines: false });
	return parsed.data || [];
}

function _serializeCsv(rows) {
	return Papa.unparse(rows, { quotes: false });
}

function _rebuildHeaderIndexes(clientData) {
	_header = _rows?.[0] || [];

	const removeIndexes = _header
		.map((h, i) => ({ h: _normHeader(h), i }))
		.filter((x) => READ_ONLY_HEADERS.has(x.h))
		.map((x) => x.i);

	if (removeIndexes.length) {
		const removeSet = new Set(removeIndexes);
		_rows = _rows.map((row) => row.filter((_, i) => !removeSet.has(i)));
		_header = _rows[0] || [];
	}

	_fieldIndex = _resolveHeaderMap(_header, _customColumnAliases(clientData));

	// Only append a brand-new canonical column for a field that has no
	// existing column under ANY known alias (HEADER_ALIASES/
	// HEADER_ALIAS_PRIORITY above). Appending unconditionally on an exact
	// literal-name miss -- the old behavior -- is what silently created
	// duplicate "Sender"/"Notes" columns alongside a client's real "Sender
	// Email"/"Description" columns (#5980, Fishbeck).
	Object.keys(FIELD_TO_HEADER).forEach((field) => {
		if (field in _fieldIndex) return;
		_header.push(FIELD_TO_HEADER[field]);
		_fieldIndex[field] = _header.length - 1;
		for (let i = 1; i < _rows.length; i++) _rows[i].push('');
	});
}

function _set(row, field, value) {
	const idx = _fieldIndex ? _fieldIndex[field] : undefined;
	if (idx >= 0) row[idx] = value == null ? '' : String(value);
}

function _get(row, field) {
	const idx = _fieldIndex ? _fieldIndex[field] : undefined;
	return idx >= 0 && row ? row[idx] : undefined;
}

function _cleanNumber(v, allowNegative = true) {
	let s = String(v || '').trim();
	if (!s) return '';
	s = s.replace(/,/g, '');
	s = allowNegative ? s.replace(/[^-.\d]/g, '') : s.replace(/[^.\d]/g, '');
	return isNaN(Number(s)) ? '' : s;
}

function _recordToRow(r, client, isACR, existingRow = null) {
	const row = existingRow ? [...existingRow] : new Array(_header.length).fill('');

	if (!r.sender && r.senderName === 'Issuer') r.sender = client;
	if (!r.receiver && r.receiverName === 'Issuer') r.receiver = client;

	// Never blank Share Class just because this particular caller's record
	// omitted it (2026-08-05, urgent fix -- the actual bug was the inline-edit
	// listeners in ledgers.js/index.js building a "full record" object without
	// it; those are now fixed at the source, but every Transaction must keep
	// exactly one Share Class value, so this stays as a second line of
	// defense against any other/future caller doing the same). Fall back to
	// whatever this row already has on disk rather than erasing it.
	_set(row, 'shareClass', r.shareClass || _get(existingRow, 'shareClass') || '');
	_set(row, 'date', r.date ? moment.unix(Number(r.date)).utc().format('YYYY-MM-DD') : '');
	_set(row, 'securityType', r.securityType || '');
	_set(row, 'notes', r.notes || '');
	_set(row, 'nrid', r.nrid || '');
	_set(row, 'senderName', r.senderName || '');
	_set(row, 'sender', r.sender || '');
	_set(row, 'receiverName', r.receiverName || '');
	_set(row, 'receiver', r.receiver || '');
	_set(row, 'quantity', _cleanNumber(r.quantity, true));
	_set(row, 'sharePrice', isACR ? '1' : _cleanNumber(r.sharePrice, false));
	_set(row, 'strikePrice', _cleanNumber(r.strikePrice, false));
	_set(row, 'expDate', r.expDate ? moment.unix(Number(r.expDate)).utc().format('YYYY-MM-DD') : '');
	_set(row, 'taxBasis', _cleanNumber(r.taxBasis, true));

	return row;
}

async function _loadOnce(clientData) {
	if (_loaded) return true;
	if (_loading) return true;

	_loading = true;

	try {
		const dataFile = clientData.transactions_data ? clientData.transactions_data : '';
		if (!dataFile) return false;

		const dataFileUrl = await getContentUrl(dataFile);
		const res = await fetch(dataFileUrl);
		const txt = await res.text();

		_rows = _parseCsv(txt);
		if (!_rows.length) _rows = [CANONICAL_HEADERS.slice()];
		_rebuildHeaderIndexes(clientData);

		_loaded = true;
		return true;
	} catch (err) {
		console.warn('TransactionService: external CSV load failed.', err);
		return false;
	} finally {
		_loading = false;
	}
}

function _findRowIndexByNrid(nrid) {
	const idx = _fieldIndex ? _fieldIndex.nrid : undefined;
	if (!(idx >= 0)) return -1;

	for (let i = 1; i < _rows.length; i++) {
		if (String(_rows[i]?.[idx] || '') === String(nrid || '')) return i;
	}
	return -1;
}

// Returns false (and applies nothing) if the record is unwritable, so
// callers can tell a refused create apart from a successful one -- this used
// to just console.error and return, which meant flushExternalQueue() still
// reported overall success with the row silently missing from the CSV.
function _applyCreate(r, client, isACR, clientData) {
	// Unlike an update, a create has no existing row to fall back to -- if
	// the caller didn't supply shareClass here, there's no way to recover
	// it, so refuse rather than silently writing a Transaction with no
	// Share Class (every Transaction must have one).
	if (!r.shareClass) {
		console.error('TransactionService: refusing to create a transaction with no shareClass.', r);
		return false;
	}

	r.nrid = uuidv4();

	const newRow = _recordToRow(r, client, isACR);
	_rows.splice(1, 0, newRow);

	if (!clientData.equity[r.shareClass].transactions) clientData.equity[r.shareClass].transactions = [];
	clientData.equity[r.shareClass].transactions.push(r);
	return true;
}

function _applyDelete(r, clientData) {
	const idx = _findRowIndexByNrid(r.nrid);
	if (idx >= 0) _rows.splice(idx, 1);

	const txns = clientData.equity[r.shareClass]?.transactions;
	if (txns) {
		const j = txns.findIndex((x) => x.nrid == r.nrid);
		if (j >= 0) txns.splice(j, 1);
	}
}

function _applyUpdate(r, client, isACR, clientData) {
	const idx = _findRowIndexByNrid(r.nrid);
	if (idx < 0) return;

	const existingRow = _rows[idx];
	_rows[idx] = _recordToRow(r, client, isACR, existingRow);

	// Same fallback as _recordToRow's shareClass guard above: if this
	// caller's record didn't carry shareClass, don't let that also make the
	// local in-memory update silently no-op against clientData.equity[undefined]
	// -- resolve against the row already on disk instead.
	const resolvedShareClass = r.shareClass || _get(existingRow, 'shareClass');
	const txns = clientData.equity[resolvedShareClass]?.transactions;
	if (txns) {
		const j = txns.findIndex((x) => x.nrid == r.nrid);
		if (j >= 0) txns[j] = r;
	}
}

/**
 * Queue a create/update/delete against the EXTERNAL_DATA CSV. Nothing is
 * written until flushExternalQueue() is called -- matches the original
 * externalTxnStore's batching behavior (bulk imports queue many rows, then
 * flush once).
 *
 * @param {object} item
 * @param {object} item.record - canonical transaction fields, including
 *   shareClass; nrid required for 'update'/'delete', assigned by
 *   flushExternalQueue for 'create'.
 * @param {'create'|'update'|'delete'} item.action
 */
export function queueExternalWrite({ record, action }) {
	_queue.push({ record, action });
}

export function hasExternalWork() {
	return _queue.length > 0;
}

// Returns false if any queued create was refused, so flushExternalQueue can
// abort the upload rather than silently persist a CSV that's missing rows.
function _applyAllQueued(client, isACR, clientData) {
	let allOk = true;
	while (_queue.length) {
		const { record, action } = _queue.shift();
		if (!record) continue;

		if (action === 'create') {
			if (!_applyCreate(record, client, isACR, clientData)) allOk = false;
		} else if (action === 'delete') _applyDelete(record, clientData);
		else if (action === 'update') _applyUpdate(record, client, isACR, clientData);
	}
	return allOk;
}

/**
 * Apply every queued write to the CSV and upload the result.
 *
 * @param {object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {object} opts.clientData - mutated in place (equity[...].transactions
 *   for every affected class) and used to locate/update transactions_data.
 * @param {boolean} [opts.isACR] - ACR clients pin sharePrice to '1' in the CSV
 *   (matches the original externalTxnStore behavior).
 * @param {(message: string) => void} [opts.onProgress] - optional UI hook;
 *   this module has no DOM/modal awareness of its own.
 * @returns {Promise<boolean>} true on success (or if there was nothing queued).
 */
export async function flushExternalQueue({ userid, cookie, client, clientData, isACR = false, onProgress }) {
	if (_flushing) return false;
	if (!hasExternalWork()) return true;

	_flushing = true;

	try {
		// Force a fresh fetch + header re-resolution right before writing
		// (2026-08-06, RSU-108/pharmajet incident) -- _loadOnce's _loaded guard
		// normally skips re-fetching for the rest of a browser tab's lifetime,
		// which is fine for reads but is exactly what let a long-lived tab
		// write using a column mapping computed against a much OLDER version
		// of the file. When the live file's header had since changed (columns
		// added/reordered/renamed by anyone, at any point after this tab first
		// loaded it) the stale _fieldIndex silently stopped recognizing real
		// columns like "Sender Email"/"Tax Basis", so _rebuildHeaderIndexes
		// appended brand-new duplicate columns instead of writing into the
		// existing ones -- corrupting the row (#pharmajet, RSU-108). Resetting
		// _loaded here means every flush re-resolves against whatever the file
		// actually looks like right now, no matter how stale this tab's first
		// load was. One extra fetch per flush is a non-issue -- flushes are
		// infrequent, deliberate admin actions, not a hot path.
		_loaded = false;
		const ok = await _loadOnce(clientData);
		if (!ok) return false;

		// GH #6140 recurrence: _apply* mutate _rows and clientData.equity[*].transactions
		// immediately, before the CSV upload below can fail. Snapshot both here and
		// restore on any failure path so a failed/partial upload doesn't leave stale balances.
		const rowsSnapshot = [..._rows];
		const equitySnapshot = {};
		for (const [shareClass, data] of Object.entries(clientData.equity || {})) {
			equitySnapshot[shareClass] = Array.isArray(data?.transactions) ? [...data.transactions] : data?.transactions;
		}
		const rollback = () => {
			_rows = rowsSnapshot;
			for (const [shareClass, txns] of Object.entries(equitySnapshot)) {
				if (!clientData.equity[shareClass]) continue;
				if (Array.isArray(txns) && Array.isArray(clientData.equity[shareClass].transactions)) {
					clientData.equity[shareClass].transactions.length = 0;
					clientData.equity[shareClass].transactions.push(...txns);
				} else {
					clientData.equity[shareClass].transactions = txns;
				}
			}
		};

		// If any queued create was refused (see _applyCreate), don't upload a
		// CSV that's silently missing rows -- report the whole flush as failed.
		if (!_applyAllQueued(client, isACR, clientData)) {
			rollback();
			return false;
		}

		const newCsv = _serializeCsv(_rows);

		try {
			onProgress?.('Uploading updated transactions file...');
			await uploadExternalTransactionsCsv({ userid, cookie, client, clientData, csv: newCsv });
		} catch (err) {
			rollback();
			throw err;
		}

		return true;
	} catch (err) {
		console.error('TransactionService: flushExternalQueue failed.', err);
		return false;
	} finally {
		_flushing = false;
	}
}

/**
 * Upload a full, already-built CSV string as a client's transactions_data
 * file: S3 upload, then PUT clientConfig/transactions-data to point at it,
 * then analytics tracking. Exported (not just used internally by
 * flushExternalQueue above) because at least one other caller -- profile.js's
 * "rename share class symbol" flow -- already has a complete, hand-mutated
 * CSV string of its own (it rewrites the class column for every matching
 * row, not a single queued create/update/delete) and previously duplicated
 * this exact upload/PUT/analytics sequence rather than sharing it.
 * @param {Object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {Object} opts.clientData - mutated in place: .transactions_data is
 *   set to the new filename once uploaded.
 * @param {string} opts.csv - the full CSV content to upload.
 */
export async function uploadExternalTransactionsCsv({ userid, cookie, client, clientData, csv }) {
	const awsFileToUpload = clientData.transactions_data
		? (Date.now() + clientData.transactions_data.substring(13, 99)).replace(/\.(xls|xlsx)$/i, '.csv')
		: Date.now() + '_' + client + '_transactions.csv';

	const res1 = await axios.get(authUploadUrl, {
		params: { filename: client + '/' + awsFileToUpload, bucket: 'reports.nthround.io', client, userid, cookie },
	});

	const formData = secureFormData(res1);
	formData.append('file', csv);
	await axios.post(res1.data.url, formData, window.axconf);

	clientData.transactions_data = awsFileToUpload;
	_persistSession({ client, clientData });

	await axios.put(
		'https://api.nthround.io/portal/clientConfig/transactions-data',
		{ body: 'body' },
		{ params: { client, userid, cookie, transactions_data: awsFileToUpload } }
	);

	if (window.analytics) {
		window.analytics.track('DataSource Updated', { userEmail: userid, workspace: client, dataSourceName: awsFileToUpload });
	}
}

/**
 * Rename the custom column's PHYSICAL CSV header from oldLabel to newLabel,
 * for EXTERNAL_DATA (CSV-backed) clients only -- JSON-native clients have no
 * header row to rename; the object key (customColumn) never changes
 * regardless of label. Caller (ledgers.js/index.js's column-settings save
 * handler) only invokes this when EXTERNAL_DATA and the label actually
 * changed; per Chris, the label must persist in the data source itself, not
 * just in clientData's config.
 *
 * Renaming is a pure header-text swap at a fixed column position -- it does
 * not move or touch any row's data, and doesn't need _rebuildHeaderIndexes()
 * to re-resolve anything, since the column's index doesn't change.
 *
 * @param {object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {object} opts.clientData
 * @param {string} opts.oldLabel - the label currently on the physical column
 * @param {string} opts.newLabel - the label to rename it to
 * @returns {Promise<boolean>} true on success, or when there was no existing
 *   physical column under oldLabel (nothing to rename -- not an error: a
 *   client enabling the custom column for the first time has no physical
 *   column yet). false only on a real failure (CSV couldn't be loaded or the
 *   renamed CSV couldn't be uploaded).
 */
export async function renameExternalCustomColumnHeader({ userid, cookie, client, clientData, oldLabel, newLabel }) {
	try {
		const ok = await _loadOnce(clientData);
		if (!ok) return false;
		if (!_header?.length) return true;

		const wantedOld = _normHeader(oldLabel);
		const idx = _header.findIndex((h) => _normHeader(h) === wantedOld);
		if (idx < 0) return true;

		_header[idx] = newLabel;
		_rows[0] = _header;

		const newCsv = _serializeCsv(_rows);
		await uploadExternalTransactionsCsv({ userid, cookie, client, clientData, csv: newCsv });

		return true;
	} catch (err) {
		console.error('TransactionService: renameExternalCustomColumnHeader failed.', err);
		return false;
	}
}

/**
 * Detect leftover JSON-based (DynamoDB-native, ClientConfiguration.equity[class].
 * transactions) records on a client whose authoritative source is now External
 * Data (CSV or Alera award-record CSV). Once transactions_data/
 * transactions_columns is set on a client, equity-transactions-scoped.py's
 * lambda_handler branches strictly if/elif/else on data source -- it never
 * reads equity[class].transactions for such a client, so any records still
 * sitting there are inert, never served to the frontend. But they're also
 * invisible clutter that can go unnoticed for a long time (onboarding-era
 * leftovers found on Clyde's account, 2026-08-02, apparently sitting since
 * initial setup) since nothing surfaces them short of manually browsing raw
 * DynamoDB. Pure and synchronous -- callers decide what to do with the result.
 * Two current callers: profile.js's existing auto-check at data-source
 * upload time, and its on-demand nthAdmin-only "check" button.
 *
 * @param {Object} clientData
 * @returns {Array<{shareClass: string, count: number}>}
 */
export function findStrayJsonTransactions(clientData) {
	return Object.keys(clientData.equity || {})
		.filter((c) => clientData.equity[c].transactions && clientData.equity[c].transactions.length > 0)
		.map((c) => ({ shareClass: c, count: clientData.equity[c].transactions.length }));
}

/**
 * Clear the JSON-based transactions found by findStrayJsonTransactions() for
 * the given share classes, and persist the change. Rolls back the in-memory
 * clientData.equity (and session storage) if the save fails, so a failed
 * network call never leaves the caller believing cleared data is saved when
 * it isn't -- same rollback shape as the rest of this module's writes.
 *
 * @param {Object} opts
 * @param {string} opts.userid
 * @param {string} opts.cookie
 * @param {string} opts.client
 * @param {Object} opts.clientData - mutated in place on success.
 * @param {Array<{shareClass: string}>} opts.staleClasses - from findStrayJsonTransactions().
 */
export async function clearStrayJsonTransactions({ userid, cookie, client, clientData, staleClasses }) {
	const previousEquity = structuredClone(clientData.equity);
	staleClasses.forEach(({ shareClass }) => {
		clientData.equity[shareClass].transactions = [];
	});
	_persistSession({ client, clientData });

	try {
		await axios.post('https://api.nthround.io/portal/clientConfig/equity', JSON.stringify({ equity: clientData.equity, userid, client, cookie }));
	} catch (error) {
		clientData.equity = previousEquity;
		_persistSession({ client, clientData });
		throw error;
	}
}

// Exported for tests only -- not part of this module's public API. The
// public write surface is queueExternalWrite/flushExternalQueue above; this
// just gives transaction-service.test.js a way to exercise the header-alias
// resolution algorithm directly, without faking a full CSV
// download/upload/axios round trip.
// Public wrappers so ledgers.js can reuse this resolver instead of its own copy.
export function resolveHeaderMap(header, extraAliases) {
	return _resolveHeaderMap(header, extraAliases);
}

export function customColumnAliases(clientData) {
	return _customColumnAliases(clientData);
}

export const _testables = { resolveHeaderMap: _resolveHeaderMap, normHeader: _normHeader };
